import React, { useEffect, useState } from 'react';
import { purchaseUnaVez } from '../utils/metaPixel';
import { purchaseGoogleUnaVez } from '../utils/googleTag';
import { avisoDeFacturacion, etiquetaDelPlan } from '../utils/estadoDeFacturacion';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { nombreParaSaludo } from '../utils/nombreParaSaludo';
import DashboardCard from '../components/dashboard/DashboardCard';
import StatusBar from '../components/dashboard/StatusBar';
import InsightsBar from '../components/dashboard/InsightsBar';
import OnboardingBanner from '../components/dashboard/OnboardingBanner';
import AlertCenter from '../components/dashboard/AlertCenter';
import VentasCard from '../components/dashboard/VentasCard';
import { alertService } from '../services/alertService';
import OrgSwitcher from '../components/organization/OrgSwitcher';
import LanguageSelector from '../components/LanguageSelector';
import { useOrganization } from '../context/OrganizationContext';
import useDashboardData from '../hooks/useDashboardData';
import useAdsEntitlement from '../hooks/useAdsEntitlement';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || "https://us-central1-sistema-bks.cloudfunctions.net";

// Helper robusto para normalizar plan
function normalizePlan(value, fallback = 'starter') {
  try {
    if (value == null) return String(fallback).toLowerCase();
    if (typeof value === 'string') return value.toLowerCase();
    if (typeof value === 'object') {
      const v = value.id ?? value.plan ?? value.name ?? fallback;
      return String(v).toLowerCase();
    }
    return String(value).toLowerCase();
  } catch {
    return String(fallback).toLowerCase();
  }
}

export default function Dashboard() {
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0);
  const navigate = useNavigate();
  const { context, loading: contextLoading, refreshOrganizations, switchContext } = useOrganization();

  // DISPLAYNAME DEL MIEMBRO DE LA ORGANIZACIÓN ACTUAL.
  //
  // Hace falta porque `clientes/{uid}.nombre` sólo lo escribe el alta de PAGO: quien
  // entra por invitación no lo tiene, y el panel le saludaba con «Hola, » —coma y nada—.
  // El nombre sí existe, en `members/{uid}.displayName`, que escribe el alta por
  // invitación derivándolo del correo.
  //
  // Se lee aquí y no se pide al servidor porque `GET /organizations` no manda
  // `displayName` en `membership` (sólo `role`, `permissions` y `joinedAt`), y añadirlo
  // sería un cambio de servidor para un saludo. Las reglas ya permiten esta lectura:
  // `firestore.rules:200`, `allow read: if isMemberOfOrg(orgId)`.
  //
  // FALLA EN SILENCIO A PROPÓSITO: si la lectura no sale, la cadena de respaldo usa el
  // correo. Un saludo no justifica un error en pantalla.
  const [displayNameMiembro, setDisplayNameMiembro] = useState(null);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!context?.orgId || !uid) { setDisplayNameMiembro(null); return; }
    let vigente = true;
    getDoc(doc(db, 'organizations', context.orgId, 'members', uid))
      .then((snap) => { if (vigente && snap.exists()) setDisplayNameMiembro(snap.data().displayName || null); })
      .catch(() => { /* se cae al respaldo del correo */ });
    return () => { vigente = false; };
  }, [context?.orgId]);
  const [waitingForOrg, setWaitingForOrg] = useState(false);
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');

  const dashboardData = useDashboardData();
  const { hasAds } = useAdsEntitlement();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        navigate('/');
        return;
      }

      // Check if there's a pending invitation first
      const pendingInviteToken = localStorage.getItem('pendingInviteToken');
      const pendingInviteUrl = localStorage.getItem('pendingInviteUrl');

      if (pendingInviteToken && pendingInviteUrl) {
        console.log('Usuario tiene invitación pendiente, esperando procesamiento...');
        setLoading(false);
        return;
      }

      // Cargar datos del cliente
      const cRef = doc(db, 'clientes', u.uid);
      const cSnap = await getDoc(cRef);
      if (cSnap.exists()) {
        setCliente({ id: u.uid, ...cSnap.data() });
      } else {
        console.log('Usuario sin plan, redirigiendo a /contratar');
        navigate('/contratar');
        return;
      }
      setLoading(false);
    });

    return () => unsub && unsub();
  }, [navigate]);

  // Post-payment: detect session_id and wait for org creation via webhook
  //
  // LA GUARDA NO PUEDE SER «¿tiene ya una organización?». Lo fue, y por eso quien YA tenía
  // una y contrataba OTRA se quedaba en la vieja: `context.isOrganization` era true, este
  // efecto salía por el return de arriba y el oyente no llegaba a engancharse nunca. El
  // webhook sí escribía la nueva en `clientes/{uid}` (`stripeWebhook.js:1284`), así que
  // aparecía en el siguiente login — pero no en la sesión en la que acababas de pagarla.
  //
  // La pregunta correcta es «¿el documento apunta ya a una organización DISTINTA de la que
  // tengo abierta?», que cubre los dos casos con la misma condición: sin organización
  // (`context.orgId` es null, cualquier orgId difiere) y con una vieja.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const sessionId = searchParams.get('session_id');

    if (!sessionId || !cliente?.id) {
      setWaitingForOrg(false);
      return;
    }

    // Sólo tapa la pantalla mientras NO haya nada que enseñar. Con una organización ya
    // abierta se espera en segundo plano: el panel de la vieja es contenido legítimo, y
    // cubrirlo con «Configurando tu espacio de trabajo…» sería esconder algo que funciona.
    setWaitingForOrg(!context?.isOrganization);

    const unsubscribe = onSnapshot(doc(db, 'clientes', cliente.id), async (snap) => {
      const data = snap.data();
      const orgId = data?.organizationContext?.currentOrgId;

      if (orgId && orgId !== context?.orgId && data?.organizationContext?.mode === 'organization') {
        unsubscribe();
        try {
          const orgs = await refreshOrganizations();
          const org = orgs.find(o => o.id === orgId);
          if (org) {
            await switchContext('organization', orgId, org);
          }
        } catch (err) {
          console.error('Error switching to org after payment:', err);
        }
        setWaitingForOrg(false);
        window.history.replaceState({}, '', '/dashboard');
      }
    });

    const timeout = setTimeout(() => {
      unsubscribe();
      setWaitingForOrg(false);
      window.history.replaceState({}, '', '/dashboard');
    }, 30000);

    return () => { unsubscribe(); clearTimeout(timeout); };
  }, [cliente?.id, context?.isOrganization, context?.orgId, refreshOrganizations, switchContext]);

  // `Purchase` del pixel de Meta — la mitad del navegador. EL PIXEL SÓLO SE CARGA AQUÍ, dentro
  // de esta rama: sin `session_id` no hay retorno de Stripe y no se carga nada. Eso preserva
  // la decisión de no meter el pixel en la herramienta de trabajo del cliente.
  //
  // SE DISPARA CON LA CONFIRMACIÓN DEL SERVIDOR, NO CON LA URL. El `?session_id=` lo pone
  // Stripe, pero cualquiera puede teclearlo. El webhook escribe `checkoutConfirmado` en
  // `clientes/{uid}` con el id de sesión y el importe REAL cobrado (`amount_total`, céntimos);
  // sólo cuando ese id coincide con el de la URL se dispara, y con ese importe — el mismo que
  // la CAPI acaba de mandar con el mismo `eventID`. Meta deduplica las dos mitades.
  //
  // Efecto APARTE del de arriba a propósito: aquél se autoprovoca (`switchContext` cambia su
  // propia dependencia) y se aquieta en cuanto la organización del documento coincide con la
  // abierta — ya no «en cuanto aparece una organización», que dejaba fuera al que compraba
  // una segunda teniendo otra. Éste sólo depende del
  // `cliente.id`, escucha hasta ver la confirmación o hasta que se agota el tiempo, y la
  // guarda de una sola vez vive en `sessionStorage` (ver `utils/metaPixel.js`).
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId || !cliente?.id) return undefined;

    const parar = onSnapshot(doc(db, 'clientes', cliente.id), (snap) => {
      const confirmacion = snap.data()?.checkoutConfirmado;
      if (confirmacion && confirmacion.sessionId === sessionId) {
        purchaseUnaVez(sessionId, confirmacion);
        // GA4 `purchase` · mismo hecho (webhook confirmado), `transaction_id` = session.id.
        purchaseGoogleUnaVez(sessionId, confirmacion);
        parar();
      }
    });
    // Si el webhook tarda más que esto, la CAPI ya habrá contado la compra por su cuenta.
    const limite = setTimeout(parar, 60000);
    return () => { parar(); clearTimeout(limite); };
  }, [cliente?.id]);

  // Subscribe to alerts summary for badge counts
  useEffect(() => {
    if (!cliente || !cliente.id) {
      setAlertsSummary(null);
      return;
    }

    // El tercer argumento es la organizationId. Sin él el resumen se leía de
    // `clientes/{uid}/alerts_summary` mientras el backend lo escribe en
    // `organizations/{org}/alerts_summary` — el mismo defecto que dejó ciegas a
    // las 7 alertas de calendario en AlertCenter.
    const unsubscribe = alertService.subscribeToSummary(
      cliente.id,
      (summary) => {
        setAlertsSummary(summary);
      },
      context?.orgId || null
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [cliente, context?.orgId]);

  // Subscribe to pending invitations count (only for owners/admins)
  useEffect(() => {
    if (!context?.isOrganization || !context?.orgId) {
      setPendingInvitationsCount(0);
      return;
    }

    const canManageMembers = context?.member?.permissions?.canManageMembers;
    if (!canManageMembers) {
      setPendingInvitationsCount(0);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'organizations', context.orgId, 'invitations'),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        setPendingInvitationsCount(snapshot.size);
      },
      (error) => {
        console.error('Error loading pending invitations:', error);
        setPendingInvitationsCount(0);
      }
    );

    return () => unsubscribe();
  }, [context?.isOrganization, context?.orgId, context?.member?.permissions?.canManageMembers]);

  if (loading || contextLoading) return <div className="loader">{tc('loading')}</div>;
  if (waitingForOrg) return <div className="loader">Configurando tu espacio de trabajo...</div>;
  if (!cliente) return null;

  const plan = context?.isOrganization
    ? normalizePlan(context.organization?.planId, 'starter')
    : normalizePlan(cliente?.plan, 'starter');

  // Organization members always have access (billing is at org level)
  const estadoValido = true;

  // Lo que se le DICE al dueño sobre la facturación. La puerta sigue en el servidor
  // (`paymentAccess`); esto usa el mismo vocabulario para no mentir en pantalla: una
  // organización cancelada decía «STARTER» y nada más (7-oct-2026). Ver utils/estadoDeFacturacion.js.
  const facturacion = context?.isOrganization ? avisoDeFacturacion(context.organization?.billing) : { aviso: null, hasta: null };
  const etiqueta = context?.isOrganization ? etiquetaDelPlan({ plan, billing: context.organization?.billing }) : { clase: plan, esCancelado: false };
  const fechaHasta = facturacion.hasta
    ? facturacion.hasta.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const { isOnboarding, setupComplete, hasChannels, hasBots } = dashboardData;

  // Badge counts
  const billingAlertsCount = alertsSummary?.categories?.billing || 0;

  // Card definitions by section
  const toolCards = [
    {
      icon: '💬',
      title: t('cards.conversations.title'),
      description: t('cards.conversations.description'),
      onClick: () => navigate('/chats'),
      variant: 'featured',
    },
    {
      icon: '📋',
      title: t('cards.campaigns.title'),
      description: t('cards.campaigns.description'),
      onClick: () => navigate('/campanas'),
      variant: 'primary',
    },
    // TEMPORAL 22-ago-2026: Ads Studio oculto hasta terminar
    // configuración y pruebas. Restaurar al activar el add-on.
    // La tarjeta se queda visible pero inerte: sin `onClick`, con
    // `disabled` (opacity 0.5 + pointer-events:none) y en la variante
    // neutra. Se descarta `locked` a propósito: pinta un candado 🔒,
    // que promete "desbloqueable" y no lo es todavía.
    // Original: onClick: () => navigate('/ads-studio'), variant: 'highlight'
    {
      icon: '📣',
      title: t('cards.adsStudio.title'),
      description: t('cards.adsStudio.description'),
      variant: 'secondary',
      disabled: true,
    },
  ];

  const configCards = [
    {
      icon: '📱',
      title: t('cards.channels.title'),
      description: t('cards.channels.description'),
      onClick: () => navigate('/canales'),
      variant: 'primary',
      badgeText: !hasChannels && !setupComplete ? t('onboarding.startHere') : '',
      badgePulse: !hasChannels && !setupComplete,
    },
    {
      icon: '🤖',
      title: t('cards.bot.title'),
      description: t('cards.bot.description'),
      onClick: () => navigate('/bots'),
      variant: 'primary',
      badgeText: !hasBots && !setupComplete ? t('onboarding.required') : '',
    },
  ];

  const accountCards = [
    {
      icon: '👥',
      title: t('cards.team.title'),
      description: t('cards.team.description'),
      badge: pendingInvitationsCount,
      badgeVariant: 'info',
      badgePulse: pendingInvitationsCount > 0,
      onClick: () => navigate('/mi-equipo'),
      variant: 'secondary',
    },
    {
      icon: '💳',
      title: t('cards.billing.title'),
      description: t('cards.billing.description'),
      badge: billingAlertsCount,
      badgeVariant: 'warning',
      onClick: () => navigate('/billing'),
      variant: 'secondary',
    },
    {
      icon: '🔌',
      title: t('cards.modules.title'),
      description: t('cards.modules.description'),
      onClick: () => navigate('/addon'),
      variant: 'secondary',
    },
  ];

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        {/* El orden de la cadena ES la decisión, así que vive en una función pura que
            se puede ejecutar en una prueba: `utils/nombreParaSaludo.js`. Sin nombre
            utilizable se usa el saludo SIN coma — «Hola, » promete algo que no llega. */}
        <h1>{(() => {
          const nombre = nombreParaSaludo({
            clienteNombre: cliente.nombre,
            displayNameMiembro,
            email: cliente.email || auth.currentUser?.email,
          });
          return nombre ? t('greeting', { name: nombre }) : t('greetingSinNombre');
        })()}</h1>
        <p className="dashboard-subtitle">{t('subtitle')}</p>
        <div className="header-actions">
          <LanguageSelector />
          <OrgSwitcher />
          <span className={`plan-badge ${etiqueta.clase}`}>{etiqueta.esCancelado ? t('billingState.canceledBadge') : plan.toUpperCase()}</span>
          <button className="logout-btn" onClick={() => auth.signOut()}>
            {tc('logout')}
          </button>
        </div>
      </header>

      {!estadoValido && (
        <div className="estado-pendiente">
          <h2>{t('pendingActivation.title')}</h2>
          <p>{t('pendingActivation.description')}</p>
          <div className="payment-buttons">
            <button className="btn-primary" onClick={() => navigate('/contratar')}>
              {t('pendingActivation.payCard')}
            </button>
          </div>
        </div>
      )}

      {facturacion.aviso === 'cancelado' && (
        <div className="estado-pendiente aviso-facturacion aviso-facturacion--cancelado">
          <h2>{t('billingState.canceled.title')}</h2>
          <p>{t('billingState.canceled.description')}</p>
          <div className="payment-buttons">
            <button className="btn-primary" onClick={() => navigate('/contratar')}>
              {t('billingState.canceled.cta')}
            </button>
          </div>
        </div>
      )}
      {facturacion.aviso === 'cancelando' && (
        <div className="estado-pendiente aviso-facturacion aviso-facturacion--cancelando">
          <h2>{fechaHasta ? t('billingState.canceling.titleUntil', { date: fechaHasta }) : t('billingState.canceling.title')}</h2>
          <p>{t('billingState.canceling.description')}</p>
          <div className="payment-buttons">
            <button className="btn-primary" onClick={() => navigate('/billing')}>
              {t('billingState.canceling.cta')}
            </button>
          </div>
        </div>
      )}
      {facturacion.aviso === 'dunning' && (
        <div className="estado-pendiente aviso-facturacion aviso-facturacion--dunning">
          <h2>{t('billingState.pastDue.title')}</h2>
          <p>{t('billingState.pastDue.description')}</p>
          <div className="payment-buttons">
            <button className="btn-primary" onClick={() => navigate('/billing')}>
              {t('billingState.pastDue.cta')}
            </button>
          </div>
        </div>
      )}

      {estadoValido && (
        <>
          {/* Status Bar */}
          <StatusBar
            name={cliente.nombre}
            integrations={dashboardData.integrations}
            channelCount={dashboardData.channelCount}
            botCount={dashboardData.botCount}
            conversationsToday={dashboardData.conversationsToday}
            botRepliesToday={dashboardData.botRepliesToday}
            setupComplete={setupComplete}
            calendarActivo={dashboardData.calendarActivo}
            reunionesHoy={dashboardData.reunionesHoy}
          />

          {/* AI Insights Bar */}
          <InsightsBar hasAds={hasAds} setupComplete={setupComplete} />

          {/* Onboarding Banner */}
          {!setupComplete && (
            <OnboardingBanner hasChannels={hasChannels} hasBots={hasBots} />
          )}

          {/* Centro de alertas.
              Vive acá y no en /statistics porque esa página está retirada desde
              el 15-jun (commit 404390e): la ruta redirige a /dashboard, así que
              AlertCenter estaba montado donde nadie puede entrar. El dashboard es
              adonde aterriza el cliente al abrir sesión (`/` → `/dashboard`).

              El "no mostrar nada si no hay nada" lo decide AlertCenter con su
              propia lista, no este componente con `alerts_summary`: ese resumen
              lo escribe un job periódico y puede estar viejo. Va ARRIBA de las
              tarjetas a propósito — si el bot dejó de responderle a un cliente,
              eso pesa más que cualquier atajo. */}
          <section className="dashboard-section">
            <AlertCenter uid={cliente.id} organizationId={context?.orgId || null} />
          </section>

          {/* Tarjeta de ventas. Va DEBAJO de las alertas y ARRIBA de las
              herramientas: una tienda caída pesa más que cuánto vendió, pero
              cuánto vendió pesa más que los atajos de navegación.

              Sólo con e-commerce conectado. `ecommerceActivo` empieza en `null`
              mientras carga, así que la comparación estricta evita que la
              tarjeta aparezca y desaparezca en el primer render. */}
          {dashboardData.ecommerceActivo === true && (
            <section className="dashboard-section">
              <VentasCard
                uid={cliente.id}
                organizationId={context?.orgId || null}
                integrations={dashboardData.integrations}
              />
            </section>
          )}

          {/* Section: Tus herramientas */}
          <section className="dashboard-section">
            <h2 className={`dashboard-section-title ${!setupComplete ? 'section-locked-title' : ''}`}>
              {!setupComplete ? t('sections.toolsLocked') : t('sections.tools')}
            </h2>
            <div className="tools-grid">
              {toolCards.map((card, index) => (
                <DashboardCard
                  key={card.title}
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                  onClick={card.onClick}
                  variant={card.variant}
                  locked={!setupComplete}
                  /* TEMPORAL 22-ago-2026: este render NO propagaba `disabled`,
                     así que marcar una tarjeta como inactiva en `toolCards` no
                     tenía ningún efecto. Las demás tarjetas no definen el campo:
                     `undefined` activa el valor por defecto (`false`) de
                     DashboardCard, así que su comportamiento no cambia. */
                  disabled={card.disabled}
                />
              ))}
            </div>
          </section>

          {/* Section: Configuración */}
          <section className="dashboard-section">
            <h2 className={`dashboard-section-title ${!setupComplete ? 'section-highlight-title' : ''}`}>
              {!setupComplete ? t('sections.configHighlight') : t('sections.config')}
            </h2>
            <div className="config-grid">
              {configCards.map((card) => (
                <DashboardCard
                  key={card.title}
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                  badge={card.badge}
                  badgeText={card.badgeText}
                  badgeVariant={card.badgeVariant}
                  badgePulse={card.badgePulse}
                  onClick={card.onClick}
                  variant={card.variant}
                />
              ))}
            </div>
          </section>

          {/* Section: Tu cuenta */}
          <section className="dashboard-section">
            <h2 className="dashboard-section-title">{t('sections.account')}</h2>
            <div className="account-grid">
              {accountCards.map((card) => (
                <DashboardCard
                  key={card.title}
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                  badge={card.badge}
                  badgeVariant={card.badgeVariant}
                  badgePulse={card.badgePulse}
                  onClick={card.onClick}
                  variant={card.variant}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
